// components/BlogPostCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import { DocumentData } from "firebase/firestore";
import { Card, Button, Chip } from "@heroui/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Calendar01Icon, User02Icon, ArrowRight01Icon, Clock01Icon } from "@hugeicons/core-free-icons";

type BlogPostCardProps = {
  post: DocumentData;
};

// Dynamically calculate reading time and strip HTML to create a clean excerpt
function getReadingTimeAndExcerpt(content: string) {
  if (!content) {
    return {
      readingTime: "2 min read",
      excerpt: "Click to read this article and explore the full content."
    };
  }
  
  const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
  const words = plainText.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 225)); // average 225 words per minute
  
  let excerpt = plainText;
  if (plainText.length > 110) {
    excerpt = plainText.substring(0, 110).trim() + "...";
  }
  
  return {
    readingTime: `${minutes} min read`,
    excerpt: excerpt || "Click to read this article and explore the full content."
  };
}

// Dynamically determine category for tag filters if not defined in database
function getCategory(post: DocumentData) {
  if (post?.category) return post.category;
  const titleLower = (post?.title || "").toLowerCase();
  
  if (titleLower.includes("devops") || titleLower.includes("cloud") || titleLower.includes("aws") || titleLower.includes("docker") || titleLower.includes("kubernetes") || titleLower.includes("ci/cd") || titleLower.includes("deployment")) {
    return "DevOps & Cloud";
  }
  if (titleLower.includes("ai") || titleLower.includes("ml") || titleLower.includes("artificial intelligence") || titleLower.includes("machine learning") || titleLower.includes("llm") || titleLower.includes("openai") || titleLower.includes("gpt")) {
    return "AI & ML";
  }
  if (titleLower.includes("database") || titleLower.includes("sql") || titleLower.includes("postgres") || titleLower.includes("mongodb") || titleLower.includes("nosql") || titleLower.includes("redis") || titleLower.includes("firestore") || titleLower.includes("firebase")) {
    return "Database";
  }
  if (titleLower.includes("frontend") || titleLower.includes("tailwind") || titleLower.includes("javascript") || titleLower.includes("typescript") || titleLower.includes("react") || titleLower.includes("next")) {
    return "Frontend";
  }
  if (titleLower.includes("backend") || titleLower.includes("api") || titleLower.includes("node") || titleLower.includes("express") || titleLower.includes("server") || titleLower.includes("rust") || titleLower.includes("go")) {
    return "Backend";
  }
  if (titleLower.includes("web") || titleLower.includes("website") || titleLower.includes("html") || titleLower.includes("css") || titleLower.includes("fullstack")) {
    return "Web Development";
  }
  if (titleLower.includes("mobile") || titleLower.includes("app") || titleLower.includes("ios") || titleLower.includes("android") || titleLower.includes("flutter") || titleLower.includes("react native") || titleLower.includes("swift")) {
    return "Mobile Development";
  }
  if (titleLower.includes("software") || titleLower.includes("system") || titleLower.includes("architecture") || titleLower.includes("pattern") || titleLower.includes("algorithm")) {
    return "Software Development";
  }
  if (titleLower.includes("design") || titleLower.includes("ui") || titleLower.includes("ux") || titleLower.includes("figma")) {
    return "Design";
  }
  if (titleLower.includes("tutorial") || titleLower.includes("guide") || titleLower.includes("how to") || titleLower.includes("build")) {
    return "Tutorial";
  }
  if (titleLower.includes("engineering") || titleLower.includes("code") || titleLower.includes("tech")) {
    return "Engineering";
  }
  if (titleLower.includes("news") || titleLower.includes("announcing") || titleLower.includes("update") || titleLower.includes("launch")) {
    return "News";
  }
  return "Insight";
}

export default function BlogPostCard({ post }: BlogPostCardProps) {
  const formattedDate = post?.createdAt
    ? (typeof post.createdAt.toDate === "function"
      ? post.createdAt.toDate().toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date(post.createdAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        }))
    : "";

  const { readingTime, excerpt } = getReadingTimeAndExcerpt(post.content);
  const category = getCategory(post);

  // Styling category color chips
  const categoryColor: Record<string, string> = {
    "Web Development": "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20",
    "Mobile Development": "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
    "Software Development": "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20",
    Frontend: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20",
    Backend: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    "DevOps & Cloud": "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    "AI & ML": "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
    Database: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    Design: "bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20",
    Engineering: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    Tutorial: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    News: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    Insight: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
  };

  const selectedCategoryStyle = categoryColor[category] || categoryColor.Insight;

  return (
    <Link href={`/blog/${post.id}`} className="group block h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-30px" }}
        transition={{ type: "spring", stiffness: 150, damping: 20 }}
        className="h-full"
      >
        <Card className="h-full border border-neutral-200/60 dark:border-zinc-800/60 bg-white/40 dark:bg-zinc-950/30 backdrop-blur-md hover:border-blue-500/40 dark:hover:border-blue-500/30 shadow-sm hover:shadow-[0_12px_30px_rgba(0,0,0,0.04)] dark:hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] transition-all duration-500 overflow-hidden rounded-[24px] flex flex-col p-3 gap-4">
          
          {/* Immersive Padded Image Container with zoom */}
          <div className="relative w-full h-48 overflow-hidden rounded-2xl bg-neutral-100 dark:bg-zinc-900">
            {post.poster ? (
              <Image
                src={post.poster}
                alt={`Cover image for ${post.title}`}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-400 dark:text-neutral-600 bg-neutral-100 dark:bg-zinc-900">
                No Cover Image
              </div>
            )}
            
            {/* Category tag overlays the image */}
            <div className="absolute top-3 left-3 z-10">
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border backdrop-blur-md ${selectedCategoryStyle}`}>
                {category}
              </span>
            </div>

            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>

          {/* Details Content Container */}
          <div className="px-3 pb-3 flex-grow flex flex-col justify-between gap-3">
            <div className="space-y-2.5">
              {/* Metadata Header */}
              <div className="flex items-center gap-3 text-xs font-outfit text-neutral-500 dark:text-zinc-400">
                <div className="flex items-center gap-1">
                  <HugeiconsIcon icon={Clock01Icon} className="w-3.5 h-3.5" size={14} />
                  <span>{readingTime}</span>
                </div>
                <span className="w-1 h-1 rounded-full bg-neutral-300 dark:bg-zinc-700" />
                <span>{formattedDate}</span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-outfit tracking-tight font-bold text-neutral-800 dark:text-neutral-150 leading-snug group-hover:text-primary transition-colors duration-300 line-clamp-2">
                {post.title}
              </h3>

              {/* Description Snippet (Clean plain-text excerpt) */}
              <p className="text-sm font-outfit text-neutral-500 dark:text-zinc-400 line-clamp-2 leading-relaxed">
                {excerpt}
              </p>
            </div>

            {/* Bottom Meta & Arrow */}
            <div className="flex items-center justify-between pt-3 border-t border-neutral-150/40 dark:border-zinc-800/40 mt-auto">
              {/* Author Info */}
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center text-[10px] font-bold text-primary font-outfit border border-blue-500/20">
                  {post.author ? post.author.substring(0, 2).toUpperCase() : "AN"}
                </div>
                <span className="text-xs font-medium text-neutral-600 dark:text-zinc-350 truncate max-w-[120px]">
                  {post.author || "Anonymous"}
                </span>
              </div>

              {/* Read button */}
              <div className="flex items-center gap-1 text-xs font-semibold text-primary font-outfit">
                <span>Read Post</span>
                <motion.span
                  className="inline-block"
                  animate={{ x: 0 }}
                  whileHover={{ x: 3 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <HugeiconsIcon 
                    icon={ArrowRight01Icon} 
                    className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" 
                    size={16} 
                  />
                </motion.span>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </Link>
  );
}



